// src/App.js
/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
// useVisibilityAwareInterval and apiFetch are now used in SettingsContext
import { AuthProvider, useAuth } from './AuthContext';
import { PublicI18nProvider } from './PublicI18nContext';
import { SettingsProvider, useSettings } from './SettingsContext';
import { OfflineProvider } from './OfflineContext';
import { GeolocationProvider } from './GeolocationContext';
import { BrowserRouter as Router, Routes, Route, Navigate, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { MapReturnBridge } from './Components/mapReturnContext';
import { Analytics } from '@vercel/analytics/react'; // added import
import OfflineIndicator from './Components/OfflineIndicator';
import './App.css';
import LoadingSpinner from './Components/Common/LoadingSpinner';
import PublicPageLayout from './Components/PublicPageLayout';
import { isPublicFocusedWorkflow } from './routeChrome';
import { EDC_HOME_PATH, isEdcAllowedPath, isEdcUser } from './edcAccess';
import SkipLink from './Components/Common/SkipLink';
import useRouteFocus from './Hooks/useRouteFocus';
import AppErrorBoundary from './Components/AppErrorBoundary';
import StatusPage from './Components/StatusPage';
import DocumentTitleManager from './Components/DocumentTitleManager';
import { TutorialProvider } from './TutorialContext';
import { PwaInstallProvider } from './PwaInstallContext';
import { AssistantProvider } from './AssistantContext';
import UpdatePrompt from './Components/UpdatePrompt';
import WhatsNewModal from './Components/WhatsNewModal';
import { checkForUpdate } from './serviceWorkerRegistration';

// Lazy load admin and heavy components to improve initial bundle size
const Sidebar = React.lazy(() => import('./Layouts/Sidebar'));
const Home = React.lazy(() => import('./Components/Home'));
const Login = React.lazy(() => import('./Components/Login'));
const ForgotPassword = React.lazy(() => import('./Components/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./Components/ResetPassword'));
const SystemTutorial = React.lazy(() => import('./Components/SystemTutorial'));
const ChatWidget = React.lazy(() => import('./Components/ChatWidget'));
const ThemePreview = React.lazy(() => import('./Components/ThemePreview'));
const About = React.lazy(() => import('./Components/About'));
const DueList = React.lazy(() => import('./Components/DueList'));
const Sir = React.lazy(() => import('./Components/Sir'));
const AddressDetail = React.lazy(() => import('./Components/AddressDetail'));
const SitusAddressDetail = React.lazy(() => import('./Components/SitusAddressDetail'));
const Contacts = React.lazy(() => import('./Components/Contacts'));
const ContactDetail = React.lazy(() => import('./Components/ContactDetail'));
const Violations = React.lazy(() => import('./Components/Violations'));
const Cases = React.lazy(() => import('./Components/Cases'));
const CaseDetail = React.lazy(() => import('./Components/CaseDetail'));
const Inspections = React.lazy(() => import('./Components/Inspections'));
const Businesses = React.lazy(() => import('./Components/Businesses'));
const CrmFollowUps = React.lazy(() => import('./Components/Crm/CrmFollowUps'));
const EdcDashboard = React.lazy(() => import('./Components/Crm/EdcDashboard'));
const EdcHome = React.lazy(() => import('./Components/Crm/EdcHome'));
const CrmSpaces = React.lazy(() => import('./Components/Crm/CrmSpaces'));
const PublicAvailableSpaces = React.lazy(() => import('./Components/Crm/PublicAvailableSpaces'));
const CrmPrograms = React.lazy(() => import('./Components/Crm/CrmPrograms'));
const CrmEventsOutreach = React.lazy(() => import('./Components/Crm/CrmEventsOutreach'));
const CrmTimelines = React.lazy(() => import('./Components/Crm/CrmTimelines'));
const CrmTimelineDetail = React.lazy(() => import('./Components/Crm/CrmTimelineDetail'));
const CrmCorridorWalk = React.lazy(() => import('./Components/Crm/CrmCorridorWalk'));
const BusinessDetail = React.lazy(() => import('./Components/BusinessDetail'));
const TownCode = React.lazy(() => import('./Components/TownCode'));
const CodeDetail = React.lazy(() => import('./Components/CodeDetail'));
const CodeEdit = React.lazy(() => import('./Components/CodeEdit'));
const Licenses = React.lazy(() => import('./Components/Licenses'));
const LicenseDetail = React.lazy(() => import('./Components/LicenseDetail'));
const Permits = React.lazy(() => import('./Components/Permits'));
const PermitDetail = React.lazy(() => import('./Components/PermitDetail'));
const Complaints = React.lazy(() => import('./Components/Complaints'));
const ComplaintDetail = React.lazy(() => import('./Components/ComplaintDetail'));
const Citations = React.lazy(() => import('./Components/Citations'));
const CitationDrafts = React.lazy(() => import('./Components/CitationDrafts'));
const CitationDetailsPage = React.lazy(() => import('./Components/CitationDetailsPage'));
const ViolationDetail = React.lazy(() => import('./Components/ViolationDetail'));
const InspectionDetail = React.lazy(() => import('./Components/InspectionDetail'));
const UnitDetail = React.lazy(() => import('./Components/Inspection/UnitDetail'));
const NewUnit = React.lazy(() => import('./Components/Inspection/NewUnit'));
const AreaDetail = React.lazy(() => import('./Components/Inspection/AreaDetail'));
const UnitAreaDetail = React.lazy(() => import('./Components/Inspection/UnitAreaDetail'));
const Rooms = React.lazy(() => import('./Components/Inspection/Rooms'));
const RoomDetail = React.lazy(() => import('./Components/Inspection/RoomDetail'));
const Users = React.lazy(() => import('./Components/Users'));
const UserDetail = React.lazy(() => import('./Components/UserDetails'));
const UnitLocationDetail = React.lazy(() => import('./Components/Unit/UnitLocationDetail'));
const Helpful = React.lazy(() => import('./Components/Helpful'));
const NewAddressPage = React.lazy(() => import('./Components/Address/NewAddressPage'));
const VacancyStatusList = React.lazy(() => import('./Components/VacancyStatusList'));
const VacancyInventory = React.lazy(() => import('./Components/VacancyInventory'));
// Field checklist screens (iPad-first), reached from My Inspections.
const FieldChecklist = React.lazy(() => import('./Components/Inspection/Field/FieldChecklist'));
const FieldReview = React.lazy(() => import('./Components/Inspection/Field/FieldReview'));
// Curb-side observation capture (phone-first) and the desk queue that answers it.
const FieldObservationCapture = React.lazy(() => import('./Components/FieldObservation/FieldObservationCapture'));
const PendingObservations = React.lazy(() => import('./Components/FieldObservation/PendingObservations'));
// Desk review of an inspection's potential violations: field record beside the
// exact code language, reached from the report page once field work is in.
const Review = React.lazy(() => import('./Components/Inspection/Review'));
const InspectionLegacyRedirect = React.lazy(() => import('./Components/Inspection/InspectionLegacyRedirect'));
const ScheduleCalendar = React.lazy(() => import('./Components/ScheduleCalendar'));
const MyInspections = React.lazy(() => import('./Components/MyInspections'));
const MapPage = React.lazy(() => import('./Components/MapPage'));
const NotificationsPage = React.lazy(() => import('./Components/NotificationsPage'));
const ResidentConcern = React.lazy(() => import('./Components/ResidentConcern'));
const LandingPage = React.lazy(() => import('./Components/LandingPage'));
const PhotoGallery = React.lazy(() => import('./Components/PhotoGallery/PhotoGallery'));
const PhotoReview = React.lazy(() => import('./Components/PhotoGallery/PhotoReview'));
const AdminDashboard = React.lazy(() => import('./Components/AdminDashboard'));
const AdminReview = React.lazy(() => import('./Components/AdminReview'));
const AdminCodeSync = React.lazy(() => import('./Components/AdminCodeSync'));
const AdminChat = React.lazy(() => import('./Components/AdminChat'));
const ApplicationsPage = React.lazy(() => import('./Components/ApplicationsPage'));
const ApplicationDetail = React.lazy(() => import('./Components/ApplicationDetail'));
const PermitApplicationsPage = React.lazy(() => import('./Components/PermitApplicationsPage'));
const PermitApplicationDetail = React.lazy(() => import('./Components/PermitApplicationDetail'));
const AdminCommentEditor = React.lazy(() => import('./Components/AdminCommentEditor'));
const LicenseApplication = React.lazy(() => import('./Components/LicenseApplication'));
const PublicLogin = React.lazy(() => import('./Components/PublicLogin'));
const PublicRegister = React.lazy(() => import('./Components/PublicRegister'));
const MyApplications = React.lazy(() => import('./Components/MyApplications'));
const PublicDashboard = React.lazy(() => import('./Components/PublicDashboard'));
const PublicPermitApplication = React.lazy(() => import('./Components/PublicPermitApplication'));
const ApplicationPaymentPage = React.lazy(() => import('./Components/ApplicationPaymentPage'));
const LicenseVerification = React.lazy(() => import('./Components/LicenseVerification'));
const AdminQuickBooks = React.lazy(() => import('./Components/AdminQuickBooks'));
const AdminPublicSupport = React.lazy(() => import('./Components/AdminPublicSupport'));
const StaffWorkloadDashboard = React.lazy(() => import('./Components/StaffWorkloadDashboard'));
const AssignmentDefaults = React.lazy(() => import('./Components/AssignmentDefaults'));
const InspectionZones = React.lazy(() => import('./Components/InspectionZones'));
const InspectorActivity = React.lazy(() => import('./Components/InspectorActivity'));
const ReportingDashboard = React.lazy(() => import('./Components/ReportingDashboard'));
const TemplateLibrary = React.lazy(() => import('./Components/TemplateLibrary'));
const AuditLogViewer = React.lazy(() => import('./Components/AuditLogViewer'));
const QuizPage = React.lazy(() => import('./Components/QuizPage'));
const QuizReviewQueue = React.lazy(() => import('./Components/QuizReviewQueue'));
const FOIAReports = React.lazy(() => import('./Components/FOIAReports'));
const EmailDashboard = React.lazy(() => import('./Components/EmailDashboard'));
const AccountPage = React.lazy(() => import('./Components/AccountPage'));
const TutorialPage = React.lazy(() => import('./Components/Tutorial/TutorialPage'));
const VehicleList = React.lazy(() => import('./Components/Vehicle/VehicleList'));
const VehicleDetail = React.lazy(() => import('./Components/Vehicle/VehicleDetail'));
const VehicleInspectionForm = React.lazy(() => import('./Components/Vehicle/VehicleInspectionForm'));
const VehicleInspectionDetail = React.lazy(() => import('./Components/Vehicle/VehicleInspectionDetail'));
const VehicleChecklistTemplate = React.lazy(() => import('./Components/Vehicle/VehicleChecklistTemplate'));
const DocumentUploadPage = React.lazy(() => import('./Components/DocumentUploadPage'));
const DocumentReviewPage = React.lazy(() => import('./Components/DocumentReviewPage'));

const TRAINING_FEATURES_ENABLED = false;
const ENVIRONMENT_LABEL = process.env.REACT_APP_ENVIRONMENT_LABEL?.trim();

const STAFF_ROUTE_PATTERNS = [
  '/about',
  '/due-list',
  '/sir',
  '/address/:id',
  '/situs-addresses/:id',
  '/units/:unitId',
  '/contacts',
  '/contacts/:id',
  '/violations',
  '/cases',
  '/case/:id',
  '/inspections',
  '/businesses',
  '/business/:id',
  '/calendar',
  '/my-inspections',
  '/map',
  '/codes',
  '/code/:id',
  '/code/:id/edit',
  '/licenses',
  '/applications',
  '/applications/:id',
  '/license/:id',
  '/permits',
  '/permit/:id',
  '/permit-applications',
  '/permit-applications/:id',
  '/complaints',
  '/complaint/:id',
  '/citations',
  '/citation-drafts',
  '/citation/:id',
  '/violation/:id',
  '/inspection/:id',
  '/inspections/:id/conduct',
  '/inspections/:id/review',
  '/inspections/:id/checklist',
  '/inspections/:id/checklist/review',
  '/inspections/:id/unit/:unitId',
  '/inspections/:id/new-unit',
  '/inspections/:id/area/:areaId',
  '/inspections/:id/unit/:unitId/area/:areaId',
  '/rooms',
  '/rooms/:id',
  '/users',
  '/users/:id',
  '/address/:addressId/unit/:unitId',
  '/helpful',
  '/new-address',
  '/vacancy-statuses',
  '/vacancies',
  '/admin',
  '/admin/review',
  '/reporting',
  '/admin/code-sync',
  '/admin-chat',
  '/admin/workload',
  '/admin/assignment-defaults',
  '/admin/inspection-zones',
  '/admin/inspector-activity',
  '/admin/audit',
  '/admin/email-dashboard',
  '/admin/email-test',
  '/templates',
  '/admin/comments/:commentId/edit',
  '/admin/quickbooks',
  '/admin/public-support',
  '/admin/public-support/:requestId',
  '/account',
  '/notifications',
  '/crm/dashboard',
  '/crm/reports',
  '/crm/follow-ups',
  '/crm/spaces',
  '/crm/programs',
  '/crm/events',
  '/crm/timelines',
  '/crm/timelines/:id',
  '/crm/corridor-walk',
  '/photo-bank',
  '/admin/foia-reports',
  '/photo-review',
  '/vehicles',
  '/vehicles/:id',
  '/document-upload',
  '/admin/document-review',
  '/vehicles/:id/inspect',
  '/vehicle-inspections/:id',
  '/admin/vehicle-checklist',
];

const PUBLIC_ACCOUNT_ROUTE_PATTERNS = [
  '/my-applications',
  '/public-dashboard',
];

function isStaffRoute(pathname) {
  return STAFF_ROUTE_PATTERNS.some((path) => matchPath({ path, end: true }, pathname));
}

function isPublicAccountRoute(pathname) {
  return PUBLIC_ACCOUNT_ROUTE_PATTERNS.some((path) => matchPath({ path, end: true }, pathname));
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <PublicI18nProvider>
          <OfflineProvider>
            <GeolocationProvider>
              <MainApp />
            </GeolocationProvider>
          </OfflineProvider>
        </PublicI18nProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

function MainApp() {
  const { user } = useAuth(); // Get user data and logout from context
  return (
    <>
      <EnvironmentBanner label={ENVIRONMENT_LABEL} />
      <OfflineIndicator />
      <UpdatePrompt />
      <Router>
        <PwaInstallProvider>
          <TutorialProvider>
            <AssistantProvider>
              <AppRoutes />
              <UpdateCheckOnNavigate />
              <AppErrorBoundary fallback={null}>
                <WhatsNewModal />
              </AppErrorBoundary>
            </AssistantProvider>
          </TutorialProvider>
        </PwaInstallProvider>
      </Router>
    </>
  );
}

// Ask the service worker to look for a new build as the user moves around
// (throttled inside checkForUpdate).
function UpdateCheckOnNavigate() {
  const location = useLocation();
  useEffect(() => {
    checkForUpdate();
  }, [location.pathname]);
  return null;
}

function EnvironmentBanner({ label }) {
  if (!label) return null;

  return (
    <div className="environment-banner" role="status" aria-label={`${label} environment`}>
      {label}
    </div>
  );
}

function AppLoadingFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <LoadingSpinner className="h-12 w-12 text-indigo-600" />
    </div>
  );
}

function AppRoutes() {
  const { user, isLoading, isSigningOut, signOutDestination, completeSignOutNavigation } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (signOutDestination && !isSigningOut) {
      navigate(signOutDestination, { replace: true, state: { signedOut: true } });
      completeSignOutNavigation();
    }
  }, [signOutDestination, isSigningOut, navigate, completeSignOutNavigation]);
  const { chatEnabled, setChatEnabled, assistantCapabilities } = useSettings();
  const location = useLocation();
  const withPublicPage = (element) => (
    <PublicPageLayout hideHelp={isPublicFocusedWorkflow(location.pathname)}>
      {element}
    </PublicPageLayout>
  );
  const returnTo = `${location.pathname}${location.search}`;
  const loginPath = `/login?from=${encodeURIComponent(returnTo)}`;
  const publicLoginPath = `/public-login?from=${encodeURIComponent(returnTo)}`;
  const isProtectedStaffRoute = isStaffRoute(location.pathname);
  const isProtectedPublicRoute = isPublicAccountRoute(location.pathname);
  const notFoundPage = (
    <StatusPage
      statusCode="404"
      eyebrow="Page not found"
      title="We could not find that page."
      message="Check the web address, or return to a known starting point."
      primaryAction={{ label: user ? 'Go to dashboard' : 'Go to homepage', to: '/' }}
      secondaryAction={
        user
          ? { label: 'View notifications', to: '/notifications' }
          : { label: 'Staff login', to: '/login' }
      }
    />
  );
  const loggedOutPage = (
    <StatusPage
      statusCode="401"
      eyebrow="Sign in required"
      title="Log back in to continue."
      message="This page is part of the staff workspace. Your session may have expired, or you may have opened a protected link before signing in."
      primaryAction={{ label: 'Log back in', to: loginPath }}
      secondaryAction={{ label: 'Go to homepage', to: '/' }}
    />
  );
  const publicLoggedOutPage = (
    <StatusPage
      statusCode="401"
      eyebrow="Sign in required"
      title="Log back in to continue."
      message="This page is part of the public portal. Your session may have expired, or you may have opened a protected link before signing in."
      primaryAction={{ label: 'Log back in', to: publicLoginPath }}
      secondaryAction={{ label: 'Go to homepage', to: '/' }}
    />
  );
  const unauthenticatedFallbackPage = isProtectedStaffRoute
    ? loggedOutPage
    : isProtectedPublicRoute
      ? publicLoggedOutPage
      : notFoundPage;
  // The assistant reads enforcement records, so EDC (role 4) does not get it.
  const showChatWidget = chatEnabled && user && user.user_type !== 'public' && user.role !== 0 && !isEdcUser(user);
  const edcRedirect = isEdcUser(user) && !isEdcAllowedPath(location.pathname);
  const capabilityEnabled = (key, defaultValue) => {
    const capability = assistantCapabilities.find((item) => item.key === key);
    return typeof capability?.enabled === 'boolean' ? capability.enabled : defaultValue;
  };
  const siteHelpEnabled = capabilityEnabled('site_help_enabled', true);
  const codeLookupEnabled = capabilityEnabled('code_lookup_enabled', true);

  if (isLoading || isSigningOut || signOutDestination) {
    return <AppLoadingFallback />;
  }

  return (
    <>
      {/* Must stay the first child: its title effect has to run before any
          route component's own useDocumentTitle/useLiteralDocumentTitle. */}
      <DocumentTitleManager isAuthenticated={!!user} />
      {user ? (
        <React.Suspense fallback={<AppLoadingFallback />}>
          <>
          <SystemTutorial />
          <Sidebar>
            <AppErrorBoundary resetKey={location.pathname}>
              <React.Suspense
                fallback={<AppLoadingFallback />}
              >
                <MapReturnBridge />
                {edcRedirect ? <Navigate to={EDC_HOME_PATH} replace /> : (
                <Routes>
                <Route path="/" element={user?.role === 0 ? <DocumentUploadPage /> : <Home />} />
                <Route path="/crm/dashboard" element={<EdcHome />} />
                <Route path="/crm/reports" element={<EdcDashboard />} />
                <Route path="/crm/follow-ups" element={<CrmFollowUps />} />
                <Route path="/crm/spaces" element={<CrmSpaces />} />
                <Route path="/crm/programs" element={<CrmPrograms />} />
                <Route path="/crm/events" element={<CrmEventsOutreach />} />
                <Route path="/crm/timelines" element={<CrmTimelines />} />
                <Route path="/crm/timelines/:id" element={<CrmTimelineDetail />} />
                <Route path="/crm/corridor-walk" element={<CrmCorridorWalk />} />
                <Route path="/available-spaces" element={<PublicAvailableSpaces />} />
                <Route path="/about" element={<About />} />
                <Route path="/due-list" element={<DueList />} />
                <Route path="/sir" element={<Sir />} />
                <Route path="/address/:id" element={<AddressDetail />} />
                <Route path="/situs-addresses/:id" element={<SitusAddressDetail />} />
                <Route path="/units/:unitId" element={<UnitLocationDetail />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/contacts/:id" element={<ContactDetail />} />
                <Route path="/violations" element={<Violations />} />
                <Route path="/cases" element={<Cases />} />
                <Route path="/case/:id" element={<CaseDetail />} />
                <Route path="/inspections" element={<Inspections />} />
                <Route path="/businesses" element={<Businesses />} />
                <Route path="/business/:id" element={<BusinessDetail />} />
                <Route path="/calendar" element={<ScheduleCalendar />} />
                <Route path="/my-inspections" element={<MyInspections />} />
                <Route path="/observations" element={<PendingObservations />} />
                <Route path="/observations/new" element={<FieldObservationCapture />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/codes" element={<TownCode />} />
                <Route path="/code/:id" element={<CodeDetail />} />
                <Route path="/code/:id/edit" element={<CodeEdit />} />
                <Route path="/licenses" element={<Licenses />} />
                <Route path="/applications" element={<ApplicationsPage />} />
                <Route path="/applications/:id" element={<ApplicationDetail />} />
                {/* TODO: Add application → license conversion step on /applications/:id — after status reaches "paid", staff need a button/flow to create the actual License record */}
                <Route path="/license/:id" element={<LicenseDetail />} />
                <Route path="/permits" element={<Permits />} />
                <Route path="/permit/:id" element={<PermitDetail />} />
                <Route path="/permit-applications" element={<PermitApplicationsPage />} />
                <Route path="/permit-applications/:id" element={<PermitApplicationDetail />} />
                <Route path="/complaints" element={<Complaints />} />
                <Route path="/complaint/:id" element={<ComplaintDetail />} />
                <Route path="/citations" element={<Citations />} />
                <Route path="/citation-drafts" element={<CitationDrafts />} />
                <Route path="/citation/:id" element={<CitationDetailsPage />} />
                <Route path="/violation/:id" element={<ViolationDetail />} />
                <Route path="/inspection/:id" element={<InspectionDetail />} />
                <Route path="/inspections/:id/checklist" element={<FieldChecklist />} />
                <Route path="/inspections/:id/checklist/review" element={<FieldReview />} />
                <Route path="/inspections/:id/conduct" element={<InspectionLegacyRedirect />} />
                <Route path="/inspections/:id/review" element={<Review />} />
                <Route path="/inspections/:id/unit/:unitId" element={<UnitDetail />} />
                <Route path="/inspections/:id/new-unit" element={<NewUnit />} />
                <Route path="/inspections/:id/area/:areaId" element={<AreaDetail />} />
                <Route
                  path="/inspections/:id/unit/:unitId/area/:areaId"
                  element={<UnitAreaDetail />}
                />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/rooms/:id" element={<RoomDetail />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<UserDetail />} />
                <Route path="/address/:addressId/unit/:unitId" element={<UnitLocationDetail />} />
                <Route path="/helpful" element={<Helpful />} />
                <Route path="/new-address" element={<NewAddressPage />} />
                <Route path="/vacancy-statuses" element={<VacancyStatusList />} />
                <Route path="/vacancies" element={<VacancyInventory />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/review" element={user?.role === 3 ? <AdminReview /> : <Navigate to="/" replace />} />
                <Route path="/reporting" element={<ReportingDashboard />} />
                <Route path="/admin/code-sync" element={<AdminCodeSync />} />
                <Route
                  path="/admin-chat"
                  element={user?.role === 3 ? <AdminChat user={user} chatEnabled={chatEnabled} setChatEnabled={setChatEnabled} /> : <Navigate to="/" replace />}
                />
                <Route path="/admin/workload" element={<StaffWorkloadDashboard />} />
                <Route
                  path="/admin/assignment-defaults"
                  element={user?.role === 3 ? <AssignmentDefaults /> : <Navigate to="/" replace />}
                />
                <Route
                  path="/admin/inspection-zones"
                  element={user?.role === 3 ? <InspectionZones /> : <Navigate to="/" replace />}
                />
                <Route path="/admin/inspector-activity" element={<InspectorActivity />} />
                <Route path="/admin/audit" element={<AuditLogViewer />} />
                <Route path="/admin/email-dashboard" element={<EmailDashboard />} />
                <Route path="/admin/email-test" element={<Navigate to="/admin/email-dashboard" replace />} />
                <Route path="/templates" element={<TemplateLibrary />} />
                <Route path="/admin/comments/:commentId/edit" element={<AdminCommentEditor />} />
                <Route
                  path="/admin/quickbooks"
                  element={<AdminQuickBooks />}
                />
                <Route path="/admin/public-support" element={<AdminPublicSupport />} />
                <Route path="/admin/public-support/:requestId" element={<AdminPublicSupport />} />
                <Route path="/document-upload" element={<DocumentUploadPage />} />
                <Route
                  path="/admin/document-review"
                  element={user?.role === 3 ? <DocumentReviewPage /> : <Navigate to="/" replace />}
                />
                <Route path="/vehicles" element={<VehicleList />} />
                <Route path="/vehicles/:id" element={<VehicleDetail />} />
                <Route path="/vehicles/:id/inspect" element={<VehicleInspectionForm />} />
                <Route path="/vehicle-inspections/:id" element={<VehicleInspectionDetail />} />
                <Route
                  path="/admin/vehicle-checklist"
                  element={user?.role === 3 ? <VehicleChecklistTemplate /> : <Navigate to="/" replace />}
                />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/tutorial" element={<TutorialPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/photo-bank" element={<PhotoGallery />} />
                {TRAINING_FEATURES_ENABLED && (
                  <>
                    <Route path="/quiz" element={<QuizPage />} />
                    <Route path="/admin/quiz-review" element={<QuizReviewQueue />} />
                  </>
                )}
                <Route
                  path="/admin/foia-reports"
                  element={[2, 3].includes(Number(user?.role)) ? <FOIAReports /> : <Navigate to="/" replace />}
                />
                <Route
                  path="/photo-review"
                  element={user?.role === 3 ? <PhotoReview /> : <Navigate to="/" replace />}
                />
                <Route path="/resident-concern" element={withPublicPage(<ResidentConcern />)} />
                <Route path="/apply" element={withPublicPage(<LicenseApplication />)} />
                <Route path="/apply/permit" element={withPublicPage(<PublicPermitApplication />)} />
                <Route path="/my-applications" element={withPublicPage(<MyApplications />)} />
                <Route path="/pay/:token" element={withPublicPage(<ApplicationPaymentPage />)} />
                <Route path="/public-dashboard" element={withPublicPage(<PublicDashboard />)} />
                <Route path="/verify-license" element={withPublicPage(<LicenseVerification />)} />
                <Route path="/theme-preview" element={<ThemePreview />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/public-login" element={<AuthenticatedPublicRedirect />} />
                <Route path="/register" element={<AuthenticatedPublicRedirect />} />
                <Route path="*" element={notFoundPage} />
                {/* Add more routes as needed */}
                </Routes>
                )}
              </React.Suspense>
            </AppErrorBoundary>
          </Sidebar>
          {showChatWidget && (
            <ChatWidget
              aiFallbackEnabled={chatEnabled}
              siteHelpEnabled={siteHelpEnabled}
              codeLookupEnabled={codeLookupEnabled}
            />
          )}
          </>
        </React.Suspense>
      ) : (
        <>
        <SkipLink />
        <RouteFocus />
        <main id="main-content" role="main" tabIndex={-1} className="min-h-screen focus:outline-none">
          <AppErrorBoundary resetKey={location.pathname}>
            <React.Suspense
              fallback={<AppLoadingFallback />}
            >
              <Routes>
               <Route path="/" element={withPublicPage(<LandingPage />)} />
               <Route path="/resident-concern" element={withPublicPage(<ResidentConcern />)} />
               <Route path="/apply" element={withPublicPage(<LicenseApplication />)} />
               <Route path="/apply/permit" element={withPublicPage(<PublicPermitApplication />)} />
               <Route path="/pay/:token" element={withPublicPage(<ApplicationPaymentPage />)} />
               <Route path="/verify-license" element={withPublicPage(<LicenseVerification />)} />
               <Route path="/available-spaces" element={withPublicPage(<PublicAvailableSpaces />)} />
               <Route path="/codes" element={withPublicPage(<TownCode />)} />
               <Route path="/code/:id" element={withPublicPage(<CodeDetail />)} />
               <Route path="/public-login" element={withPublicPage(<PublicLogin />)} />
               <Route path="/register" element={withPublicPage(<PublicRegister />)} />
              <Route path="/theme-preview" element={<ThemePreview />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
               <Route path="*" element={withPublicPage(unauthenticatedFallbackPage)} />
              </Routes>
            </React.Suspense>
          </AppErrorBoundary>
        </main>
        </>
      )}
      <Analytics /> {/* render here so it's always mounted while Router is active */}
    </>
  );
}

// Scopes useRouteFocus to the branch that renders it (hooks can't be
// called conditionally inside AppRoutes).
function RouteFocus() {
  useRouteFocus();
  return null;
}

function AuthenticatedPublicRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get('from');
  const publicRedirect = from && from.startsWith('/') && !from.startsWith('//') ? from : '/public-dashboard';
  const safeRedirect = user?.user_type === 'public' ? publicRedirect : '/';

  return <Navigate to={safeRedirect} replace />;
}

export default App;
